import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Plus, Pencil, Trash2, ListChecks, Search, ExternalLink } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { franceCompetencesUrl } from '../lib/certifications';
import { ChipsObservationAdmin } from '../components/ChipsObservationAdmin';
import { QuestionsSatisfactionAdmin } from '../components/QuestionsSatisfactionAdmin';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LISTES = [
  {
    key: 'domaines', label: 'Domaines de formation', endpoint: 'domaines',
    fields: [
      { key: 'abreviation', label: 'Abréviation', required: true, width: 'w-32' },
      { key: 'nom', label: 'Nom', required: true },
    ],
  },
  {
    key: 'nsf', label: 'Codes NSF', endpoint: 'codes-nsf',
    fields: [
      { key: 'code', label: 'Code', required: true, width: 'w-24' },
      { key: 'label', label: 'Libellé', required: true },
    ],
  },
  {
    key: 'lieux', label: 'Lieux de formation', endpoint: 'lieux',
    fields: [
      { key: 'nom', label: 'Nom', required: true },
      { key: 'adresse', label: 'Adresse' },
      { key: 'ville', label: 'Ville', width: 'w-40' },
      { key: 'code_postal', label: 'Code postal', width: 'w-28' },
    ],
  },
  {
    key: 'types', label: 'Types de formation', endpoint: 'types-formation',
    fields: [
      { key: 'value', label: 'Code', required: true, width: 'w-36' },
      { key: 'label', label: 'Libellé', required: true },
    ],
  },
  {
    key: 'certifications', label: 'Certifications', endpoint: 'certifications',
    rowLink: { build: franceCompetencesUrl, title: 'Voir la fiche France Compétences' },
    fields: [
      {
        key: 'type_repertoire', label: 'Répertoire', type: 'choice', width: 'w-28',
        options: [
          { value: 'RNCP', label: 'RNCP' },
          { value: 'RS', label: 'RS (Répertoire spécifique)' },
        ],
      },
      { key: 'code_rncp', label: 'Code', required: true, width: 'w-32', mono: true },
      { key: 'titre', label: 'Titre', required: true },
      { key: 'code_nsf', label: 'Code NSF', width: 'w-24' },
      { key: 'organisme_certificateur', label: 'Organisme' },
      { key: 'date_echeance', label: 'Échéance', type: 'date', width: 'w-32' },
      { key: 'url_fiche', label: 'URL fiche', hideInTable: true },
    ],
  },
  {
    key: 'sequences', label: 'Séquences', endpoint: 'sequences', idKey: 'code',
    fields: [
      { key: 'code', label: 'Code', required: true, width: 'w-32', mono: true },
      { key: 'name', label: 'Nom', required: true },
      { key: 'nb_modules', label: 'Modules', width: 'w-24', tableOnly: true },
    ],
  },
];

const ListeCrud = ({ config }) => {
  const idKey = config.idKey || 'id';
  const formFields = config.fields.filter(f => !f.tableOnly);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchItems = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/${config.endpoint}`);
      setItems(res.data);
    } catch { toast.error('Erreur lors du chargement'); }
  }, [config.endpoint]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openDialog = (item = null) => {
    setEditing(item);
    const empty = Object.fromEntries(formFields.map(f => [f.key, f.type === 'choice' ? f.options[0].value : '']));
    setForm(item ? { ...empty, ...item } : empty);
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API_URL}/${config.endpoint}/${encodeURIComponent(editing[idKey])}`, { ...editing, ...form });
        toast.success('Élément modifié');
      } else {
        await axios.post(`${API_URL}/${config.endpoint}`, form);
        toast.success('Élément créé');
      }
      setDialogOpen(false);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/${config.endpoint}/${encodeURIComponent(deleteTarget[idKey])}`);
      toast.success('Élément supprimé');
      setItems(prev => prev.filter(i => i[idKey] !== deleteTarget[idKey]));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la suppression');
    } finally { setDeleteTarget(null); }
  };

  const visibleFields = config.fields.filter(f => !f.hideInTable);
  const filtered = items.filter(i =>
    !search || config.fields.some(f => String(i[f.key] || '').toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <Card className="border-0 shadow-sm">
      <CardContent className="p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-xs">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              className="pl-9 h-9"
              placeholder="Rechercher..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid={`search-${config.key}`}
            />
          </div>
          <span className="text-xs text-slate-400">{filtered.length} élément(s)</span>
          <Button size="sm" className="ml-auto bg-indigo-600 hover:bg-indigo-700 gap-1.5" onClick={() => openDialog()} data-testid={`btn-add-${config.key}`}>
            <Plus className="w-4 h-4" /> Ajouter
          </Button>
        </div>

        <div className="border rounded-lg overflow-hidden">
          <div
            className="grid gap-3 bg-slate-50 px-3 py-2 text-xs text-slate-500 uppercase tracking-wide font-medium"
            style={{ gridTemplateColumns: `${visibleFields.map(f => f.width ? '0.5fr' : '1fr').join(' ')} 80px` }}
          >
            {visibleFields.map(f => <span key={f.key}>{f.label}</span>)}
            <span />
          </div>
          <div className="divide-y">
            {filtered.map(item => (
              <div
                key={item[idKey]}
                className="grid gap-3 items-center px-3 py-2 text-sm hover:bg-slate-50"
                style={{ gridTemplateColumns: `${visibleFields.map(f => f.width ? '0.5fr' : '1fr').join(' ')} 80px` }}
                data-testid={`liste-row-${item[idKey]}`}
              >
                {visibleFields.map((f, i) => (
                  <span key={f.key} className={`break-words ${f.mono || i === 0 ? 'font-mono text-indigo-600 font-medium' : 'text-slate-700'}`} title={f.type !== 'choice' ? (item[f.key] ?? '') : undefined}>
                    {f.type === 'choice' ? (
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold font-sans ${(item[f.key] || 'RNCP') === 'RS' ? 'bg-violet-100 text-violet-700' : 'bg-amber-100 text-amber-700'}`}>
                        {item[f.key] || 'RNCP'}
                      </span>
                    ) : (item[f.key] ?? '—')}
                  </span>
                ))}
                <div className="flex items-center justify-end gap-1">
                  {config.rowLink && config.rowLink.build(item) && (
                    <a
                      href={config.rowLink.build(item)}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={config.rowLink.title}
                      className="inline-flex items-center justify-center h-7 w-7 rounded-md text-indigo-500 hover:text-indigo-700 hover:bg-indigo-50 transition-colors"
                      data-testid={`btn-link-${item[idKey]}`}
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openDialog(item)} data-testid={`btn-edit-${item[idKey]}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500 hover:text-red-700" onClick={() => setDeleteTarget(item)} data-testid={`btn-delete-${item[idKey]}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
            {filtered.length === 0 && (
              <p className="text-center py-8 text-slate-400 text-sm">Aucun élément</p>
            )}
          </div>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editing ? 'Modifier' : 'Ajouter'} — {config.label}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSave} className="space-y-3">
              {formFields.map(f => (
                <div key={f.key} className="space-y-1.5">
                  <Label>{f.label}{f.required && ' *'}</Label>
                  {f.type === 'choice' ? (
                    <RadioGroup
                      value={form[f.key] || f.options[0].value}
                      onValueChange={(v) => setForm({ ...form, [f.key]: v })}
                      className="flex gap-5 pt-1"
                      data-testid={`radio-${config.key}-${f.key}`}
                    >
                      {f.options.map(o => (
                        <div key={o.value} className="flex items-center gap-2">
                          <RadioGroupItem value={o.value} id={`${f.key}-${o.value}`} data-testid={`radio-option-${o.value}`} />
                          <Label htmlFor={`${f.key}-${o.value}`} className="font-normal cursor-pointer">{o.label}</Label>
                        </div>
                      ))}
                    </RadioGroup>
                  ) : (
                    <Input
                      type={f.type || 'text'}
                      value={form[f.key] || ''}
                      onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                      required={f.required}
                      data-testid={`input-${config.key}-${f.key}`}
                    />
                  )}
                </div>
              ))}
              <DialogFooter className="pt-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
                <Button type="submit" disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid={`btn-save-${config.key}`}>
                  {editing ? 'Enregistrer' : 'Créer'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
              <AlertDialogDescription>
                Cet élément sera définitivement supprimé de la liste "{config.label}".
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={handleDelete} data-testid="confirm-delete-liste">
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
};

export default function ListesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const validTabs = [...LISTES.map(l => l.key), 'chips', 'satisfaction'];
  const tabParam = searchParams.get('tab');
  const activeTab = validTabs.includes(tabParam) ? tabParam : 'domaines';

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto space-y-6" data-testid="listes-page">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-indigo-100 flex items-center justify-center">
            <ListChecks className="w-6 h-6 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Listes</h1>
            <p className="text-sm text-slate-500">Gérez les listes de référence utilisées dans les menus déroulants</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={(v) => setSearchParams({ tab: v })}>
          <TabsList className="bg-white border shadow-sm flex-wrap h-auto">
            {LISTES.map(l => (
              <TabsTrigger key={l.key} value={l.key} data-testid={`tab-liste-${l.key}`}>{l.label}</TabsTrigger>
            ))}
            <TabsTrigger value="chips" data-testid="tab-liste-chips">Chips d'observation</TabsTrigger>
            <TabsTrigger value="satisfaction" data-testid="tab-liste-satisfaction">Questionnaires de satisfaction</TabsTrigger>
          </TabsList>
          {LISTES.map(l => (
            <TabsContent key={l.key} value={l.key}>
              <ListeCrud config={l} />
            </TabsContent>
          ))}
          <TabsContent value="chips"><ChipsObservationAdmin /></TabsContent>
          <TabsContent value="satisfaction"><QuestionsSatisfactionAdmin /></TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
